import { useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";

import { register } from "../../api/auth";
import useRegisterSteps from "../../hooks/useRegisterSteps";

import StepUser from "./StepUser";
import StepStore from "./StepStore";
import StepAgency from "./StepAgency";
import StepAgent from "./StepAgent";

import HomeIconButton from "../../components/misc/HomeIconButton";
import LanguageSwitcher from "../../components/misc/LanguageSwitcher";
import ThemeToggle from "../../components/misc/ThemeToggle";
import Button from "../../components/ui/Button";

import { LanguageContext } from "../../context/LanguageContext";

export default function Register() {
  const { t, isRTL } = useContext(LanguageContext);
  const navigate = useNavigate();

  const {
    step,
    role,
    setRole,
    userData,
    updateUserData,
    profileData,
    updateProfileData,
    errors,
    updateErrors,
    nextStep,
    prevStep,
    resetRegister,
  } = useRegisterSteps();

  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [countdown, setCountdown] = useState(3);

  /* =========================
      STEPS CONFIG
  ========================= */
  const totalSteps = 2;
  const progress = Math.round((step / totalSteps) * 100);

  /* =========================
      REDIRECT AFTER SUCCESS
  ========================= */
  useEffect(() => {
    if (!done) return;
    if (countdown === 0) navigate("/login");

    const timer = setTimeout(() => {
      setCountdown((c) => c - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [done, countdown, navigate]);

  /* =========================
      SUBMIT
  ========================= */
  const handleSubmit = async () => {
    setLoading(true);

    let payload;
    const hasFile =
      profileData?.logo ||
      profileData?.verification_document ||
      profileData?.file;

    if (hasFile) {
      payload = new FormData();
      Object.entries(userData).forEach(([k, v]) => payload.append(k, v));
      payload.append("role", role);
      Object.entries(profileData).forEach(([k, v]) => payload.append(k, v));
    } else {
      payload = { ...userData, role, ...profileData };
    }

    try {
      await register(payload);
      toast.success(t("register.success"));
      setDone(true);
      resetRegister();
    } catch (err) {
      const backend = err.response?.data || {};
      Object.entries(backend).forEach(([k, v]) => {
        updateErrors({ [k]: v[0] });
        toast.error(v[0]);
      });
    } finally {
      setLoading(false);
    }
  };

  /* =========================
      SUCCESS SCREEN
  ========================= */
  if (done) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950 px-4"
        dir={isRTL ? "rtl" : "ltr"}
      >
        <div className="w-full max-w-md bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-8 text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 flex items-center justify-center mb-4">
            
          </div>

          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            {t("register.success_title")}
          </h2>

          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
            {t("register.redirecting")} {countdown}s
          </p>

          <Button onClick={() => navigate("/login")}>
            {t("register.go_to_login")}
          </Button>
        </div>
      </div>
    );
  }

  /* =========================
      MAIN FORM
  ========================= */
  return (
    <div
      className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950 px-4 py-8 sm:py-12"
      dir={isRTL ? "rtl" : "ltr"}
    >
      {/* TOP ACTIONS */}
      <div
        className={`absolute top-3 sm:top-4 flex gap-2 sm:gap-3 ${
          isRTL ? "left-3 sm:left-4" : "right-3 sm:right-4"
        }`}
      >
        <HomeIconButton />
        <LanguageSwitcher />
        <ThemeToggle />
      </div>

      {/* CARD */}
      <div className="w-full max-w-xl bg-white dark:bg-gray-900 rounded-xl sm:rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm">
        {/* HEADER */}
        <div className="px-4 sm:px-6 pt-6 sm:pt-8 pb-4 sm:pb-6 text-center">
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white">
            {t("register.title")}
          </h1>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            {t("register.subtitle")}
          </p>
        </div>

        {/* PROGRESS */}
        <div className="px-4 sm:px-6 mt-4 sm:mt-0">
          <div className="flex justify-between text-xs text-gray-500 mb-2">
            <span>
              {t("register.step")} {step}/{totalSteps}
            </span>
            <span>{progress}%</span>
          </div>

          <div className="h-1.5 bg-gray-200 dark:bg-gray-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-600 transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* CONTENT */}
        <div className="px-4 sm:px-6 py-6 sm:py-8">
          {step === 1 && (
            <StepUser
              userData={userData}
              updateUserData={updateUserData}
              errors={errors}
              updateErrors={updateErrors}
              role={role}
              setRole={setRole}
              nextStep={nextStep}
            />
          )}

          {step === 2 && role === "STORE" && (
            <StepStore
              profileData={profileData}
              updateProfileData={updateProfileData}
              prevStep={prevStep}
              submit={handleSubmit}
              loading={loading}
              errors={errors}
              updateErrors={updateErrors}
            />
          )}

          {step === 2 && role === "AGENCY_OWNER" && (
            <StepAgency
              profileData={profileData}
              updateProfileData={updateProfileData}
              prevStep={prevStep}
              submit={handleSubmit}
              loading={loading}
              errors={errors}
              updateErrors={updateErrors}
            />
          )}

          {step === 2 && role === "AGENCY_AGENT" && (
            <StepAgent
              profileData={profileData}
              updateProfileData={updateProfileData}
              prevStep={prevStep}
              submit={handleSubmit}
              loading={loading}
            />
          )}
        </div>

        {/* FOOTER */}
        <div className="border-t border-gray-200 dark:border-gray-800 px-4 sm:px-6 py-4 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t("register.have_account")}{" "}
            <button
              onClick={() => navigate("/login")}
              className="text-blue-600 hover:underline font-medium"
            >
              {t("register.login_here")}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}